import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Wrench, CreditCard, FileBox, Layers, Settings, LogOut, Shield, FlaskConical, BarChart2, Mail, Phone } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/tenants', icon: Building2, label: 'Tenants' },
  { to: '/services', icon: Wrench, label: 'Services' },
  { to: '/features', icon: Layers, label: 'Features' },
  { to: '/billing', icon: CreditCard, label: 'Billing' },
  { to: '/templates', icon: FileBox, label: 'Templates' },
  { to: '/template-studio', icon: FlaskConical, label: 'Template Studio' },
  { to: '/ai-quality', icon: BarChart2, label: 'AI Quality' },
  { to: '/platform-email', icon: Mail, label: 'Platform Email' },
  { to: '/twilio-numbers', icon: Phone, label: 'Twilio Numbers' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const comingSoonItems = [];

export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        {/* Brand */}
        <div className="px-6 py-5 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-brand-400" />
            <div>
              <h1 className="text-lg font-bold">Sixty10</h1>
              <p className="text-xs text-gray-400">Super Admin</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}

          {/* Coming Soon items */}
          <div className="pt-4 mt-4 border-t border-gray-700">
            {comingSoonItems.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 cursor-not-allowed">
                <Icon className="w-5 h-5" />
                {label}
                <span className="ml-auto text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">Soon</span>
              </div>
            ))}
          </div>
        </nav>

        {/* User / Logout */}
        <div className="px-3 py-4 border-t border-gray-700">
          <div className="px-3 py-2 text-sm text-gray-400 truncate">
            {user?.email}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
